import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { BuyerContactInfoCompact, BuyerContactInfoFull } from "@/components/seller/BuyerContactInfo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Search, Package, Truck, CheckCircle, Clock, AlertCircle, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/shared/PageHeader";
import { PullToRefresh } from "@/components/shared/PullToRefresh";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { OrderCountdownTimer, getCountdownStatus } from "@/components/orders/OrderCountdownTimer";

export default function SellerOrders() {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        // Fetch buyer profiles in bulk (no FK required)
        const buyerIds = Array.from(new Set(data.map((o: any) => o.buyer_id).filter(Boolean)));
        let buyersMap: Record<string, any> = {};
        if (buyerIds.length > 0) {
          const { data: buyers } = await supabase
            .from('profiles')
            .select('id, full_name, username, phone')
            .in('id', buyerIds);
          buyersMap = (buyers || []).reduce((acc: any, b: any) => {
            acc[b.id] = b;
            return acc;
          }, {});
        }
        setOrders(data.map((order: any) => {
          // Parse shipping_address if it's a string
          let shippingAddress = order.shipping_address;
          if (typeof shippingAddress === 'string') {
            try {
              shippingAddress = JSON.parse(shippingAddress);
            } catch {
              shippingAddress = null;
            }
          }
          
          return {
            id: order.id,
            buyerId: order.buyer_id,
            status: order.status || 'pending',
            paymentStatus: order.payment_status || 'pending',
            totalAmount: Number(order.total_amount),
            paymentReference: order.payment_reference,
            createdAt: new Date(order.created_at),
            items: order.items || [],
            shippingAddress: shippingAddress,
            deliveryAddress: order.delivery_address,
            deliveryType: order.delivery_type || 'regular',
            recipientName: order.recipient_name,
            recipientPhone: order.recipient_phone,
            customerName: shippingAddress?.fullName || order.recipient_name || 
                          buyersMap[order.buyer_id]?.full_name || buyersMap[order.buyer_id]?.username || 
                          'Guest Customer',
            customerPhone: shippingAddress?.phone || order.recipient_phone || 
                          buyersMap[order.buyer_id]?.phone || 'N/A',
            customerEmail: shippingAddress?.email || null,
          };
        }));
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast({
        title: "Error",
        description: "Failed to fetch orders",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id, toast]);

  const { isRefreshing, pullDistance, triggerRefresh } = usePullToRefresh({
    onRefresh: fetchOrders,
  });

  const handleUpdateStatus = async (orderId: string, newStatus: string) => {
    try {
      const order = orders.find(o => o.id === orderId);
      
      // Auto-confirm if shipping a pending order
      if ((newStatus === 'shipped') && order?.status === 'pending') {
        await supabase
          .from('orders')
          .update({ 
            status: 'confirmed',
            updated_at: new Date().toISOString()
          })
          .eq('id', orderId);
      }
      
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (error) throw error;

      // Update local state
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));

      // Create tracking entry for status update (non-blocking)
      if (newStatus === 'shipped' || newStatus === 'delivered') {
        const trackingNumber = `TRK${orderId.slice(0, 8).toUpperCase()}`;
        
        try {
          // Check if tracking record exists
          const { data: existingTracking } = await supabase
            .from('tracking')
            .select('id')
            .eq('order_id', orderId)
            .maybeSingle();

          if (existingTracking) {
            // Update existing record
            await supabase
              .from('tracking')
              .update({
                tracking_number: trackingNumber,
                status: newStatus === 'shipped' ? 'in_transit' : 'delivered',
                description: newStatus === 'shipped' ? 'Order has been shipped' : 'Order has been delivered',
                location: 'Lagos, Nigeria',
                updated_at: new Date().toISOString()
              })
              .eq('order_id', orderId);
          } else {
            // Insert new record
            await supabase
              .from('tracking')
              .insert({
                order_id: orderId,
                tracking_number: trackingNumber,
                status: newStatus === 'shipped' ? 'in_transit' : 'delivered',
                description: newStatus === 'shipped' ? 'Order has been shipped' : 'Order has been delivered',
                location: 'Lagos, Nigeria'
              });
          }
        } catch (trackingError) {
          console.error('Non-blocking tracking update failed:', trackingError);
          // Continue - don't fail the main operation
        }
      }

      toast({
        title: "Status Updated",
        description: `Order status updated to ${newStatus}`,
      });
    } catch (error) {
      console.error('Error updating order status:', error);
      toast({
        title: "Error",
        description: "Failed to update order status",
        variant: "destructive",
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4" />;
      case "confirmed":
        return <Package className="h-4 w-4" />;
      case "shipped":
        return <Truck className="h-4 w-4" />;
      case "delivered":
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "secondary";
      case "confirmed":
        return "outline";
      case "shipped":
        return "default";
      case "delivered":
        return "success";
      case "cancelled":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    return status === "completed" ? "success" : "secondary";
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          order.paymentReference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          order.customerName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="py-10 text-center">
            <p>Loading orders...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <PullToRefresh pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <PageHeader
        title="Order Management"
        description="Manage and track your customer orders"
        backTo="/seller/dashboard"
        onRefresh={triggerRefresh}
        isRefreshing={isRefreshing}
      />
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Orders</CardTitle>
              <CardDescription>{filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''} found</CardDescription>
            </div>
            <div className="flex gap-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search orders..."
                  className="pl-10 w-64"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredOrders.length === 0 ? (
            <div className="text-center py-10">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-muted-foreground">No orders found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Deadline</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="font-mono text-sm">
                      {order.id.slice(0, 8).toUpperCase()}
                    </TableCell>
                    <TableCell>
                      <BuyerContactInfoCompact
                        customerName={order.customerName}
                        customerPhone={order.customerPhone}
                        deliveryType={order.deliveryType}
                        orderStatus={order.status}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {order.items.map((item: any, idx: number) => (
                          <div key={idx}>
                            {item.quantity}x {item.productName || item.product?.title || 'Item'}
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold">
                      ₦{order.totalAmount.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getPaymentStatusColor(order.paymentStatus) as any}>
                        {order.paymentStatus}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={order.status}
                        onValueChange={(value) => handleUpdateStatus(order.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <OrderCountdownTimer 
                        orderCreatedAt={order.createdAt} 
                        orderStatus={order.status}
                        deadlineDays={2}
                      />
                    </TableCell>
                    <TableCell>{order.createdAt.toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedOrder(order);
                          setIsOrderModalOpen(true);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Order Details Modal */}
      <Dialog open={isOrderModalOpen} onOpenChange={setIsOrderModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Order Details - {selectedOrder?.id.slice(0, 8).toUpperCase()}</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Customer Info with Privacy Controls */}
              <BuyerContactInfoFull
                customerName={selectedOrder.customerName}
                customerPhone={selectedOrder.customerPhone}
                customerEmail={selectedOrder.customerEmail}
                deliveryAddress={selectedOrder.deliveryAddress}
                shippingAddress={selectedOrder.shippingAddress}
                deliveryType={selectedOrder.deliveryType}
                orderStatus={selectedOrder.status}
              />

              {/* Order Items */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Order Items</h3>
                <div className="space-y-4">
                  {selectedOrder.items.map((item: any, idx: number) => (
                    <div key={idx} className="flex items-center gap-4 p-4 border rounded-lg">
                      {item.product?.images?.[0] && (
                        <img
                          src={item.product.images[0]}
                          alt={item.product.title}
                          className="w-16 h-16 object-cover rounded-md"
                        />
                      )}
                      <div className="flex-grow">
                        <h4 className="font-medium">{item.productName || item.product?.title || 'Item'}</h4>
                        <p className="text-sm text-muted-foreground">Quantity: {item.quantity}</p>
                        <p className="text-sm text-muted-foreground">Price: ₦{item.price?.toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">₦{((item.price || 0) * item.quantity).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Order Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Order Status</p>
                    <Badge variant={getStatusColor(selectedOrder.status) as any}>
                      {selectedOrder.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Status</p>
                    <Badge variant={getPaymentStatusColor(selectedOrder.paymentStatus) as any}>
                      {selectedOrder.paymentStatus}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Amount</p>
                    <p className="font-semibold text-lg">₦{selectedOrder.totalAmount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Order Date</p>
                    <p className="font-medium">{selectedOrder.createdAt.toLocaleDateString()}</p>
                  </div>
                </div>
                
                {selectedOrder.paymentReference && (
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Reference</p>
                    <p className="font-mono text-xs">{selectedOrder.paymentReference}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}